// Farbbeitrag zum Aktualisieren-Button (docs/architecture/color-catalog-revisions.md, F7).
// Veraltet ist eine im Auftrag verwendete Farbe, deren dargestellte Revision
// (Katalogstand + ausdrückliche Bindungen des Auftrags, F2) nicht der aktuellen
// Revision entspricht. Aktualisieren setzt den Katalogstand des Auftrags auf den
// aktuellen Stand; die Darstellung aller Ebenen folgt, ohne Systemdaten zu ändern.
import { S } from '../app/app_state.js?v=20260817c';
import {
  colorCatalogState,
  colorRevisionKey,
  currentColorRevisionNumber,
  ensureColorCatalogsForProject,
  findColor,
  findCurrentColor,
  resolveColorLabel,
  resolvedColorRevisionNumber,
  sanitizeColorBinding,
  withColorBindingContext
} from '../../../colors/color_catalog_registry.js?v=20260720a';
import { collectColorReferences } from '../../../colors/color_order_usage.js';
import { colorPickerSwatchStyle } from '../../../colors/color_picker.js?v=20260720a';
import { COLOR_CATALOG_BINDING_FIELD } from './color_catalog_binding.js';

const LEGACY_BINDING = Object.freeze({ state: null, bindings: Object.freeze({}) });

export function orderColorBinding(project = S.project) {
  return sanitizeColorBinding(project?.[COLOR_CATALOG_BINDING_FIELD]) || LEGACY_BINDING;
}

function sheetsOf(context) {
  if (Array.isArray(context?.sheets) && context.sheets.length) return context.sheets;
  return S.project ? [{ id: '', title: '', number: 1, active: true, project: S.project }] : [];
}

// Verwendete Farbschlüssel je Ebene: Map key -> [{ sheet, count }].
export function orderColorUsage(context) {
  const usage = new Map();
  for (const sheet of sheetsOf(context)) {
    for (const [key, count] of collectColorReferences(sheet.project, value => colorRevisionKey(value))) {
      if (!usage.has(key)) usage.set(key, []);
      usage.get(key).push({ sheet, count });
    }
  }
  return usage;
}

function outdatedKeys(context) {
  if (colorCatalogState() === null) return [];
  const binding = orderColorBinding(context?.project || S.project);
  return [...orderColorUsage(context).keys()].filter(key => (
    resolvedColorRevisionNumber(key, binding) !== currentColorRevisionNumber(key)
  )).sort((a, b) => a.localeCompare(b, 'de'));
}

function splitKey(key) {
  const separator = key.indexOf(':');
  return [key.slice(0, separator), key.slice(separator + 1)];
}

function describeChanges(before, after) {
  const changes = [];
  if (!before) return ['Eintrag neu im Katalog'];
  if (before.name !== after.name) changes.push(`Name: ${before.name || '–'} → ${after.name || '–'}`);
  if (String(before.hex || '').toUpperCase() !== String(after.hex || '').toUpperCase()) changes.push(`Farbwert: ${before.hex || '–'} → ${after.hex || '–'}`);
  const image = entry => entry?.texture?.image || entry?.swatchImage || '';
  if (image(before) !== image(after)) changes.push('Bild/Textur geändert');
  if (JSON.stringify(before.materialSurface || null) !== JSON.stringify(after.materialSurface || null)) changes.push('Oberfläche geändert');
  return changes.length ? changes : ['Weitere Angaben geändert'];
}

function sheetLabel(sheet) {
  const title = String(sheet.title || '').trim();
  return title ? `${sheet.number ? `${sheet.number} ` : ''}${title}` : `Ebene ${sheet.number || 1}`;
}

export const colorOrderUpdateProvider = Object.freeze({
  id: 'colors',
  label: 'Farben, Dekore und Texturen',
  count(context) {
    return outdatedKeys(context).length;
  },
  async collect(context) {
    // Nachladen: große Kataloge (NCS/Dekore) aller Ebenen und die Revisionen des
    // Katalogstands bzw. der Bindungen dieses Auftrags.
    for (const sheet of sheetsOf(context)) await ensureColorCatalogsForProject(sheet.project);
    const project = context?.project || S.project;
    await ensureColorCatalogsForProject({ [COLOR_CATALOG_BINDING_FIELD]: project?.[COLOR_CATALOG_BINDING_FIELD] });
    const binding = orderColorBinding(project);
    const usage = orderColorUsage(context);
    return outdatedKeys(context).map(key => {
      const [catalog, code] = splitKey(key);
      const before = withColorBindingContext(binding, () => findColor(catalog, code));
      const after = withColorBindingContext(null, () => findCurrentColor(catalog, code));
      const value = { catalog: after?.catalog || catalog, code: after?.code || code };
      return {
        key,
        label: withColorBindingContext(null, () => resolveColorLabel(value, code)),
        before: {
          revision: resolvedColorRevisionNumber(key, binding),
          label: withColorBindingContext(binding, () => resolveColorLabel(value, code)),
          swatchStyle: withColorBindingContext(binding, () => colorPickerSwatchStyle(value))
        },
        after: {
          revision: currentColorRevisionNumber(key),
          label: withColorBindingContext(null, () => resolveColorLabel(value, code)),
          swatchStyle: withColorBindingContext(null, () => colorPickerSwatchStyle(value))
        },
        details: [
          ...describeChanges(before, after || {}),
          `Verwendet: ${(usage.get(key) || []).map(({ sheet, count }) => `${sheetLabel(sheet)} (${count}×)`).join(', ')}`
        ]
      };
    });
  },
  // Der ganze Auftrag erhält den aktuellen Katalogstand; ausdrückliche Bindungen
  // sind damit überflüssig. Undo stellt die bisherige Bindung wieder her.
  apply(items, context) {
    const state = colorCatalogState();
    const project = context?.project || S.project;
    if (!project || state === null || !items?.length) return false;
    project[COLOR_CATALOG_BINDING_FIELD] = { schemaVersion: 1, state, bindings: {} };
    return true;
  }
});
